import React from 'react';

import { InfoIcon, Tooltip } from 'common/components';
import { TooltipPlacementType } from 'common/components/Tooltip/Tooltip.component';

import { useBowser } from 'common/hooks';

import './index.css';

interface IFormFieldInfoLabel {
  className?: string;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacementType;
  'data-test'?: string;
}

interface IFormFieldInfoValue {
  className?: string;
  tooltip?: React.ReactNode;
  'data-test'?: string;
}

interface IFormFieldInfoValueSuffix {
  className?: string;
  'data-test'?: string;
}

interface IFormFieldInfo {
  Label: React.FC<React.PropsWithChildren<IFormFieldInfoLabel>>;
  Value: React.FC<React.PropsWithChildren<IFormFieldInfoValue>> & {
    Suffix: React.FC<React.PropsWithChildren<IFormFieldInfoValueSuffix>>;
  };
}

export const FormFieldInfo: React.FC<React.PropsWithChildren<{ className?: string; 'data-test'?: string }>> &
  IFormFieldInfo = ({ children, className = '', 'data-test': dataAttribute }) => (
  <div data-test={dataAttribute} className={`form-field-info ${className}`}>
    {children}
  </div>
);

const FormFieldLabel: React.FC<React.PropsWithChildren<IFormFieldInfoLabel>> = ({
  children,
  className = '',
  tooltip = '',
  tooltipPlacement = 'right',
  'data-test': dataAttribute,
}) => {
  const { isDesktop } = useBowser();
  return (
    <span data-test={dataAttribute} className={`form-field-info__label ${className}`}>
      {children}
      {tooltip && (
        <Tooltip enabled overlay={tooltip} placement={tooltipPlacement} overlayStyle={{ maxWidth: 300 }}>
          <span className='flex'>
            <InfoIcon type={isDesktop ? 'primary' : 'coin'} />
          </span>
        </Tooltip>
      )}
    </span>
  );
};

const FormFieldValue: React.FC<React.PropsWithChildren<IFormFieldInfoValue>> & {
  Suffix: React.FC<React.PropsWithChildren<IFormFieldInfoValueSuffix>>;
} = ({ children, className = '', tooltip, 'data-test': dataAttribute }) => {
  if (tooltip) {
    return (
      <span className={`form-field-info__value form-field-info__value--tooltiped ${className}`}>
        <Tooltip enabled overlay={tooltip} placement='topRight'>
          <span data-test={dataAttribute} className='flex gap-2'>
            {children}
          </span>
        </Tooltip>
      </span>
    );
  }
  return (
    <span data-test={dataAttribute} className={`form-field-info__value ${className}`}>
      {children}
    </span>
  );
};

const FormFieldValueSuffix: React.FC<React.PropsWithChildren<IFormFieldInfoValueSuffix>> = ({
  children,
  className = '',
  'data-test': dataAttribute,
}) => (
  <span data-test={dataAttribute} className={`form-field-info__value-suffix ${className}`}>
    {children}
  </span>
);

FormFieldValue.Suffix = FormFieldValueSuffix;

FormFieldInfo.Label = FormFieldLabel;
FormFieldInfo.Value = FormFieldValue;
